import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { PrivateFileImage } from '../components/PrivateFileAccess';
import { ArrowLeft, MapPin, Clock, DollarSign, Star, MessageCircle } from 'lucide-react';
import { SERVICE_CATEGORIES } from '../constants/categories';

const JobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user, API, language } = useContext(AuthContext);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedBid, setSelectedBid] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState('');

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}`);
      setJob(response.data);
    } catch (error) {
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const acceptBid = async (bid) => {
    try {
      // Accept bid and assign professional directly (no payment)
      await axios.put(`${API}/bids/${bid.id}/accept`);
      toast.success(language === 'en' ? 'Bid accepted! Professional assigned. Opening chat...' : language === 'it' ? 'Offerta accettata! Professionista assegnato. Apertura chat...' : language === 'fr' ? 'Offre acceptée ! Professionnel assigné. Ouverture du chat...' : 'Angebot angenommen! Profi zugewiesen. Chat wird geöffnet...');
      
      // Wait a moment then navigate to chat
      setTimeout(() => {
        navigate(`/chat/${jobId}`);
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'en' ? 'Failed to accept bid' : language === 'it' ? 'Impossibile accettare l\'offerta' : language === 'fr' ? 'Impossible d\'accepter l\'offre' : 'Angebot konnte nicht angenommen werden'));
    }
  };

  const handleCompleteJob = async () => {
    if (rating === 0) {
      toast.error(language === 'en' ? 'Please select a rating' : language === 'it' ? 'Seleziona una valutazione' : language === 'fr' ? 'Veuillez sélectionner une note' : 'Bitte wählen Sie eine Bewertung');
      return;
    }

    try {
      await axios.post(`${API}/jobs/${jobId}/complete`, {
        rating,
        review
      });
      toast.success(language === 'en' ? 'Job marked as complete!' : language === 'it' ? 'Lavoro segnato come completato!' : language === 'fr' ? 'Travail marqué comme terminé !' : 'Auftrag als abgeschlossen markiert!');
      setShowRatingModal(false);
      fetchJobDetails(); // Refresh job details
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'en' ? 'Failed to complete job' : language === 'it' ? 'Impossibile completare il lavoro' : language === 'fr' ? 'Impossible de terminer le travail' : 'Auftrag konnte nicht abgeschlossen werden'));
    }
  };

  const handlePaymentSuccess = () => {
    setShowPayment(false);
    setSelectedBid(null);
    toast.success(language === 'en' ? 'Payment successful! Professional assigned.' : language === 'it' ? 'Pagamento riuscito! Professionista assegnato.' : language === 'fr' ? 'Paiement réussi ! Professionnel assigné.' : 'Zahlung erfolgreich! Profi zugewiesen.');
    fetchJobDetails();
  };

  const handlePaymentCancel = () => {
    setShowPayment(false);
    setSelectedBid(null);
  };

  const content = {
    en: {
      jobDetails: 'Job Details',
      postedBy: 'Posted by',
      budget: 'Budget',
      urgency: 'Urgency',
      location: 'Location',
      status: 'Status',
      description: 'Description',
      photos: 'Photos',
      bids: 'Bids',
      noBids: 'No bids yet',
      acceptBid: 'Accept',
      viewProfile: 'View Profile',
      chatWithClient: 'Chat with Client',
      chatWithPro: 'Chat with Professional',
      assignedTo: 'Assigned to',
      markComplete: 'Mark as Complete',
      jobCompleted: 'Job Completed',
      yourRating: 'Your Rating',
      rateJob: 'Rate this Job',
      rating: 'Rating',
      selectRating: 'Click to rate',
      review: 'Review (Optional)',
      reviewPlaceholder: 'Share your experience...',
      submit: 'Submit Rating',
      cancel: 'Cancel'
    },
    de: {
      jobDetails: 'Auftragsdetails',
      postedBy: 'Erstellt von',
      budget: 'Budget',
      urgency: 'Dringlichkeit',
      location: 'Standort',
      status: 'Status',
      description: 'Beschreibung',
      photos: 'Fotos',
      bids: 'Angebote',
      noBids: 'Noch keine Angebote',
      acceptBid: 'Annehmen',
      viewProfile: 'Profil anzeigen',
      chatWithClient: 'Chat mit Kunde',
      chatWithPro: 'Chat mit Profi',
      assignedTo: 'Zugewiesen an',
      markComplete: 'Als Abgeschlossen Markieren',
      jobCompleted: 'Auftrag Abgeschlossen',
      yourRating: 'Ihre Bewertung',
      rateJob: 'Auftrag Bewerten',
      rating: 'Bewertung',
      selectRating: 'Klicken Sie zum Bewerten',
      review: 'Rezension (Optional)',
      reviewPlaceholder: 'Teilen Sie Ihre Erfahrung...',
      submit: 'Bewertung Abgeben',
      cancel: 'Abbrechen'
    },
    it: {
      jobDetails: 'Dettagli Lavoro',
      postedBy: 'Pubblicato da',
      budget: 'Budget',
      urgency: 'Urgenza',
      location: 'Posizione',
      status: 'Stato',
      description: 'Descrizione',
      photos: 'Foto',
      bids: 'Offerte',
      noBids: 'Nessuna offerta ancora',
      acceptBid: 'Accetta',
      viewProfile: 'Visualizza Profilo',
      chatWithClient: 'Chat con Cliente',
      chatWithPro: 'Chat con Professionista',
      assignedTo: 'Assegnato a',
      markComplete: 'Segna come Completato',
      jobCompleted: 'Lavoro Completato',
      yourRating: 'La Tua Valutazione',
      rateJob: 'Valuta questo Lavoro',
      rating: 'Valutazione',
      selectRating: 'Clicca per valutare',
      review: 'Recensione (Opzionale)',
      reviewPlaceholder: 'Condividi la tua esperienza...',
      submit: 'Invia Valutazione',
      cancel: 'Annulla'
    },
    fr: {
      jobDetails: 'Détails de l\'Emploi',
      postedBy: 'Publié par',
      budget: 'Budget',
      urgency: 'Urgence',
      location: 'Emplacement',
      status: 'Statut',
      description: 'Description',
      photos: 'Photos',
      bids: 'Offres',
      noBids: 'Aucune offre encore',
      acceptBid: 'Accepter',
      viewProfile: 'Voir le Profil',
      chatWithClient: 'Chat avec Client',
      chatWithPro: 'Chat avec Professionnel',
      assignedTo: 'Attribué à',
      markComplete: 'Marquer comme Terminé',
      jobCompleted: 'Emploi Terminé',
      yourRating: 'Votre Évaluation',
      rateJob: 'Évaluer cet Emploi',
      rating: 'Évaluation',
      selectRating: 'Cliquez pour évaluer',
      review: 'Avis (Optionnel)',
      reviewPlaceholder: 'Partagez votre expérience...',
      submit: 'Soumettre l\'Évaluation',
      cancel: 'Annuler'
    }
  };

  const t = content[language] || content.en;

  // Helper function to translate category
  const getCategoryName = (categoryValue) => {
    const category = SERVICE_CATEGORIES.find(c => c.value === categoryValue);
    if (!category) return categoryValue;
    return language === 'en' ? category.en : language === 'it' ? category.it : language === 'fr' ? category.fr : category.de;
  };

  // Helper function to translate urgency
  const getUrgencyLabel = (urgency) => {
    const urgencyMap = {
      en: { low: 'Low', medium: 'Medium', high: 'High', emergency: 'Emergency' },
      de: { low: 'Niedrig', medium: 'Mittel', high: 'Hoch', emergency: 'Notfall' },
      it: { low: 'Bassa', medium: 'Media', high: 'Alta', emergency: 'Emergenza' },
      fr: { low: 'Faible', medium: 'Moyen', high: 'Élevé', emergency: 'Urgence' }
    };
    return urgencyMap[language]?.[urgency] || urgencyMap.en[urgency] || urgency;
  };

  // Helper function to translate status
  const getStatusLabel = (status) => {
    const statusMap = {
      en: { open: 'Open', assigned: 'Assigned', completed: 'Completed', cancelled: 'Cancelled' },
      de: { open: 'Offen', assigned: 'Zugewiesen', completed: 'Abgeschlossen', cancelled: 'Abgebrochen' },
      it: { open: 'Aperto', assigned: 'Assegnato', completed: 'Completato', cancelled: 'Annullato' },
      fr: { open: 'Ouvert', assigned: 'Attribué', completed: 'Terminé', cancelled: 'Annulé' }
    };
    return statusMap[language]?.[status] || statusMap.en[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Job not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="job-details-page">
      <div className="max-w-4xl mx-auto px-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {language === 'en' ? 'Back' : language === 'it' ? 'Indietro' : language === 'fr' ? 'Retour' : 'Zurück'}
        </Button>

        <Card className="swiss-shadow-lg mb-6">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl font-heading mb-2">{job.title}</CardTitle>
                <p className="text-sm text-gray-500">{t.postedBy}: {job.client_name}</p>
              </div>
              <Badge className="text-lg">{getStatusLabel(job.status)}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Job Info */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-xs text-gray-500">{t.budget}</p>
                  <p className="font-bold">CHF {job.budget}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="text-xs text-gray-500">{t.urgency}</p>
                  <p className="font-medium">{getUrgencyLabel(job.urgency)}</p>
                </div>
              </div>
              {job.location?.address && (
                <div className="flex items-center space-x-2">
                  <MapPin className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="text-xs text-gray-500">{t.location}</p>
                    <p className="font-medium text-sm">{job.location.address}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <h3 className="font-bold mb-2">{t.description}</h3>
              <p className="text-gray-700">{job.description}</p>
            </div>

            {/* Photos */}
            {job.photos && job.photos.length > 0 && (
              <div>
                <h3 className="font-bold mb-2">{t.photos}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {job.photos.map((photo, idx) => (
                    <PrivateFileImage key={idx} reference={photo} alt={`job-${idx}`} className="w-full h-32 object-cover rounded" testId={`job-private-photo-${idx}`} />
                  ))}
                </div>
              </div>
            )}

            {/* Assigned Professional */}
            {job.status === 'assigned' && job.assigned_pro_name && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">{t.assignedTo}</p>
                <p className="font-bold text-lg">{job.assigned_pro_name}</p>
                <div className="flex gap-2 mt-3">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => navigate(`/chat/${job.id}`)}
                    data-testid="chat-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    {user.role === 'client' ? t.chatWithPro : t.chatWithClient}
                  </Button>
                  {user.role === 'client' && (
                    <Button 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => setShowRatingModal(true)}
                      data-testid="complete-job-btn"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      {t.markComplete}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Completed Job */}
            {job.status === 'completed' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">{t.jobCompleted}</p>
                <p className="font-bold text-lg">{job.assigned_pro_name}</p>
                {job.rating && (
                  <div className="flex items-center mt-2">
                    <p className="text-sm text-gray-600 mr-2">{t.yourRating}:</p>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star} 
                          className={`w-5 h-5 ${star <= job.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bids Section (for clients) */}
        {user.role === 'client' && job.status === 'open' && (
          <Card className="swiss-shadow-lg">
            <CardHeader>
              <CardTitle className="font-heading">{t.bids} ({job.bids?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              {!job.bids || job.bids.length === 0 ? (
                <p className="text-gray-500 text-center py-8">{t.noBids}</p>
              ) : (
                <div className="space-y-4">
                  {job.bids.map((bid) => (
                    <div key={bid.id} className="border rounded-lg p-4 hover:border-blue-600" data-testid={`bid-${bid.id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center space-x-3">
                          <Avatar>
                            <AvatarFallback>{bid.pro_name[0]}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-bold">{bid.pro_name}</p>
                              {bid.profile && bid.profile.ratings_avg >= 4.5 && bid.profile.total_reviews >= 5 && (
                                <Badge className="bg-yellow-500 text-white text-xs">
                                  ⭐ {language === 'en' ? 'Top Rated' : language === 'it' ? 'Top Valutato' : language === 'fr' ? 'Mieux noté' : 'Top Bewertet'}
                                </Badge>
                              )}
                            </div>
                            {bid.profile && (
                              <div className="flex items-center space-x-1 text-sm text-gray-500">
                                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                <span>{bid.profile.ratings_avg.toFixed(1)}</span>
                                <span>({bid.profile.total_reviews} {language === 'en' ? 'reviews' : language === 'it' ? 'recensioni' : language === 'fr' ? 'avis' : 'Bewertungen'})</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-xl text-blue-600">CHF {bid.proposed_price}</p>
                        </div>
                      </div>
                      {bid.message && (
                        <p className="text-sm text-gray-600 mb-3">{bid.message}</p>
                      )}
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => acceptBid(bid)}
                          data-testid={`accept-bid-${bid.id}`}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          {t.acceptBid}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate(`/professional/${bid.pro_id}`)}
                        >
                          {t.viewProfile}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Payment Dialog */}
      {/* Rating Modal */}
      <Dialog open={showRatingModal} onOpenChange={setShowRatingModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t.rateJob}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Star Rating */}
            <div>
              <p className="text-sm font-medium mb-3">{t.rating}</p>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="transition-transform hover:scale-110"
                  >
                    <Star 
                      className={`w-10 h-10 ${
                        star <= (hoverRating || rating) 
                          ? 'text-yellow-500 fill-yellow-500' 
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              <p className="text-center text-sm text-gray-500 mt-2">
                {rating > 0 ? `${rating} ${rating === 1 ? 'star' : 'stars'}` : t.selectRating}
              </p>
            </div>

            {/* Review Text */}
            <div>
              <label className="text-sm font-medium mb-2 block">{t.review}</label>
              <textarea
                value={review}
                onChange={(e) => setReview(e.target.value)}
                placeholder={t.reviewPlaceholder}
                className="w-full p-3 border rounded-lg min-h-[100px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">{review.length}/500</p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRatingModal(false);
                  setRating(0);
                  setReview('');
                }}
                className="flex-1"
              >
                {t.cancel}
              </Button>
              <Button
                onClick={handleCompleteJob}
                disabled={rating === 0}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {t.submit}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDetails;